import { hasPermissions, type Permission } from '@gestao/shared'
import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'

import { LoadingScreen } from '../../app/loading-screen'
import { useSessionStore } from '../../lib/session'
import { NoAccessPage } from '../placeholder/no-access-page'
import { locationPath, type ReturnToState } from './return-to'

interface RequireAuthProps {
  /** A tela de troca de senha obrigatória é a única liberada com a troca pendente. */
  allowPendingPassword?: boolean
  children: ReactNode
}

/**
 * Rotas protegidas: sem sessão vai para o login (guardando a rota de retorno); com senha
 * temporária pendente vai para /trocar-senha. Reage a mudanças da sessão (expiração, saída
 * em outra aba), não só à primeira navegação.
 */
export function RequireAuth({ allowPendingPassword = false, children }: RequireAuthProps) {
  const location = useLocation()
  const status = useSessionStore((state) => state.status)
  const mustChangePassword = useSessionStore((state) => state.user?.mustChangePassword ?? false)
  const endReason = useSessionStore((state) => state.endReason)

  if (status === 'unknown') return <LoadingScreen />

  if (status === 'anonymous') {
    // Quem saiu por vontade própria volta ao login sem rota de retorno.
    const state: ReturnToState | undefined =
      endReason === 'signed-out' ? undefined : { from: locationPath(location) }
    return <Navigate to="/login" replace state={state} />
  }

  if (mustChangePassword && !allowPendingPassword) {
    return (
      <Navigate
        to="/trocar-senha"
        replace
        state={{ from: locationPath(location) } satisfies ReturnToState}
      />
    )
  }

  return children
}

interface RequirePermissionProps {
  permission: Permission
  children: ReactNode
}

/** Mostra "Sem acesso" quando o perfil não tem a permissão da rota. */
export function RequirePermission({ permission, children }: RequirePermissionProps) {
  const allowed = useSessionStore((state) =>
    state.user ? hasPermissions(state.user.permissions, permission) : false,
  )
  return allowed ? children : <NoAccessPage />
}

/** Como `RequirePermission`, com qualquer uma das permissões (a carga de leads aceita `sales.manage`). */
export function RequireAnyPermission({
  permissions,
  children,
}: {
  permissions: readonly Permission[]
  children: ReactNode
}) {
  const allowed = useSessionStore((state) => {
    const granted = state.user?.permissions
    return granted ? permissions.some((permission) => hasPermissions(granted, permission)) : false
  })
  return allowed ? children : <NoAccessPage />
}
